<script setup lang="ts">
import { useUIStore } from '~~/stores/uiStore'

useAuth()

const pwaEnabled = usePwaFeatureFlag()
const uiStore = useUIStore()

// Brand-aware <head> w runtime: <html data-brand>, title template, theme-color,
// apple title, favicon, OG site_name — czyta aktywny brand (useBrand). Wydzielone
// do useBrandHead() (testowalne w izolacji).
useBrandHead()

// Segmentify (per-brand, bezpośrednie osadzenie skryptów w <head>). No-op gdy
// segmentifyAccountId puste. Tu ładujemy TYLKO SDK — kontener widgetu wyszukiwarki
// (#segmentify-search) renderuje wyłącznie strona /segmentify-search, żeby ten selektor
// nie istniał na pozostałych trasach. Zob. app/composables/useSegmentify.ts.
useSegmentify()

// <link rel="manifest"> — zastępuje <VitePwaManifest />. Wskazuje na DYNAMICZNY,
// per-brand /site.webmanifest (server/routes/site.webmanifest.get.ts), a nie statyczny
// /manifest.webmanifest, który @vite-pwa zapieka z brandem build-time (jeden obraz
// Dockera → na youlook byłby manifest lavmag). Client-only + bramkowane pwaEnabled tak
// samo jak dawny <VitePwaManifest /> (pwaEnabled zależy od visitorId w localStorage —
// nie może wpływać na współdzielony SSR HTML).
if (import.meta.client) {
  useHead({
    link: computed(() =>
      pwaEnabled.value ? [{ rel: 'manifest', href: '/site.webmanifest' }] : [],
    ),
  })
}
</script>

<template>
  <NuxtLoadingIndicator color="var(--color-promo)" />
  <NuxtRouteAnnouncer />
  <AppToast />
  <!-- Lazy: modal quick-view nie jest potrzebny przy pierwszym renderze (otwierany
       dopiero na interakcję). Wyjęty z chunku startowego → mniej JS do hydracji
       (TBT/INP). Nuxt sam dzieli komponent z prefiksem Lazy na osobny chunk. -->
  <LazyProductQuickViewModal />
  <!--
    Baner zgody na cookies — MUSI być w ClientOnly.
    Trasy stron mają swr w routeRules (nuxt.config.ts) → Nitro cache'uje SSR HTML
    per-URL i serwuje identyczny wszystkim, NIE wariuje po cookie. Render serwerowy
    useCookie jest więc bezużyteczny: stan banera (zwykle „widoczny", bo cold-fill
    robi crawler/user bez cookie) zapieka się w cache i trafia do każdego, a u tych
    co już zdecydowali hydracja czyta ich cookie → baner znika → MIGNIĘCIE.
    ClientOnly = baner nigdy nie wchodzi do cache'owanego HTML; widoczność liczona
    wyłącznie po stronie klienta z document.cookie. NIE usuwać tego opakowania.
  -->
  <ClientOnly>
    <CookieConsentBanner />
  </ClientOnly>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>

  <!-- PWA components — gated by feature flag; client-only (browser APIs). -->
  <ClientOnly>
    <template v-if="pwaEnabled">
      <!-- <link rel="manifest"> wstrzykiwany w <script setup> (dynamiczny /site.webmanifest);
           <VitePwaManifest /> usunięty, bo wskazywał statyczny /manifest.webmanifest (brand build-time). -->
      <!-- Modal sam zarządza widocznością (store.installModalOpen); auto-trigger
           wewnątrz respektuje shouldShowInstallPrompt(), a ręczny przycisk /
           prompt po zakupie otwierają go bezpośrednio. -->
      <LazyInstallPromptModal />
      <LazyPushPermissionSheet
        v-if="uiStore.pushSheetContext"
        :context="uiStore.pushSheetContext"
        @close="uiStore.closePushPermissionSheet()"
      />
      <LazyUpdateAvailableBanner />
    </template>
  </ClientOnly>
</template>
